/* eslint-disable no-throw-literal */
import React, { useEffect, useRef } from "react";
import { fetchTimeout, saveJobBasic, useInterval, RehydrateThumbnails, encodeJobNumber } from "../../components/Common";
import { API_BASE } from "../../AppConfig";
import {
    updateDesigner,
    updateAPP,
    SHOW_DESIGNER_MESSAGE,
    setDesignerLoadingBackdrop,
    UPDATE_DESIGNER,
    SHOW_LOADING_BACKDROP,
    SHOW_CUSTOMER_JOB_PANEL,
} from "../../redux_store/appState";
import { updateJobParameter, updateJobIfRequired, updateCustomer, updateJobState } from "../../redux_store/jobState";
import { loadOffering } from "../../redux_store/configState";
import { getD365CustomerCodes } from "../../helper/Helper";
import { isBeforePriceRiseStart, isDeliveryDateInvalid, isPriceRiseActive } from "../../helper/PriceRise";

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { ErpEditorInternal } from "../../components_main/ErpTable";

function useDesignerHooks({
    dispatch,
    job,
    app,
    config,
    user_data,
    triggerCustoomerFormClose,
    job_distributor,
    SHOW_DIALOG,
    colour_change,
    shape_update,
    setNewWidth,
    setNewHeight,
    setDialogData,
    setOpenDialog,
    setShouldSave,
    shouldSave,
    openMessageBox,
    setTriggerCustoomerFormClose,
    quoteParams,
    openDeliveryDateDialog,
    setIsResizingEW,
    isResizingEW,
    setIsResizingNS,
    isResizingNS,
    framePosition,
    bomOpen,
    setBomIsPopOut,
}) {
    const job_loaded = !!job?.id;
    const { priceRise } = app;

    const isResizingEWRef = useRef(false);
    const isResizingNSRef = useRef(false);
    const framePositionRef = useRef(framePosition);

    useEffect(() => {
        if (!job_loaded) return;

        if (!isPriceRiseActive(app)) return;
        if (!isBeforePriceRiseStart(app)) return;
        if (!isDeliveryDateInvalid(job)) return;

        const startStr = priceRise?.price_rise_start_date || "";

        const timer = setTimeout(() => {
            dispatch({
                type: SHOW_DESIGNER_MESSAGE,
                message: {
                    message: `Price Rise Planned ${startStr}. Please set estimated Delivery Date`,
                    type: "warning",
                    timeout: 20000,
                    action: {
                        label: "Set Date",
                        callback: openDeliveryDateDialog,
                    },
                },
            });
        }, 5000);
        return () => clearTimeout(timer);
    }, [job_loaded]);

    // Visibility listener
    useEffect(() => {
        const onVisibilityChange = () => {
            if (!document.hidden) {
                dispatch(updateJobIfRequired(job?.App?.Locked ?? false));
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [job?.App?.Locked]);

    // Brand redirect
    useEffect(() => {
        if (job?.id) {
            const encodedJobId = encodeJobNumber(job.id);
            const currentBrandRedirect = window.location.href.split("app/")[0] + "app?3d-viewer&job=" + encodedJobId;
            if (job.brandRedirect !== currentBrandRedirect) {
                dispatch(updateJobParameter("brandRedirect", currentBrandRedirect));
            }
        }
    }, [job?.id]);

    // Pricing Tier logic
    useEffect(() => {
        if (!job_loaded) return;

        let get_tier = false;
        const customerLevel = job?.Customer?.DistLevel;
        const distributorAccount = job?.Distributor?.LYVESAccount;

        // Determine customer tier
        if (job?.Distributor?.Type === "BBC") {
            // Prefer customer level if available (code may have been changed)
            get_tier = customerLevel || distributorAccount;
        } else if (app?.user?.Role >= 0 && job?.Distributor) {
            get_tier = distributorAccount;
        }

        // If a valid customer level exists (not Retail, not same as distributor)
        // always try to fetch updated codes from D365
        if (
            customerLevel &&
            customerLevel !== "Retail" &&
            customerLevel !== distributorAccount &&
            job?.Distributor?.Type !== "BBC"
        ) {
            get_tier = getD365CustomerCodes({
                job,
                app,
                config,
                dispatch,
                updateCustomer,
                get_tier: distributorAccount,
            });
        }

        if (!get_tier) return;

        // Always fetch tier prices if LYVESAccount changed
        if (
            !app?.pricefile_tier ||
            app?.pricefile_tier?.LYVESAccount !== get_tier ||
            (job?.QuoteParameters?.DeliveryDate &&
                new Date(job?.QuoteParameters?.DeliveryDate).toDateString() !==
                    new Date(app?.pricefile_tier?.cacheDate).toDateString())
        ) {
            dispatch(setDesignerLoadingBackdrop(`Loading Prices for Pricing Account: ${get_tier}`));
            const formattedDate = [
                new Date().getDate(),
                new Date().getMonth() + 1,
                String(new Date().getFullYear()).slice(-2),
            ]
                .map((n) => String(n).padStart(2, "0"))
                .join("/");

            const config_url = `${API_BASE()}admin/GetePricing?account=${encodeURIComponent(
                get_tier
            )}&date=${encodeURIComponent(job?.QuoteParameters?.DeliveryDate || formattedDate)}`;
            fetchTimeout(config_url, { method: "GET" })
                .then(async (response) => {
                    if (!response.ok) throw new Error();
                    const json = await response.json();
                    json.LYVESAccount = get_tier;

                    if (json?.cacheDate) {
                        const cacheDateObj = new Date(json.cacheDate);
                        const now = new Date();
                        const ageInDays = (now - cacheDateObj) / (1000 * 60 * 60 * 24);

                        // Friendly formatted date
                        const formattedDate = cacheDateObj.toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        });

                        // If cache date is older than 4 weeks ? show everyone
                        if (ageInDays > 28) {
                            dispatch({
                                type: SHOW_DESIGNER_MESSAGE,
                                message: {
                                    message: `Pricing data is over a month old. Please verify or refresh. (${formattedDate})`,
                                    type: "error",
                                },
                            });
                        } else {
                            // Always show the cached date for dev/test
                            if (
                                app?.user?.Role >= 99 &&
                                app?.user?.id.includes("diego") &&
                                ["localhost", "test"].some((word) => window.location.href.includes(word))
                            ) {
                                dispatch({
                                    type: SHOW_DESIGNER_MESSAGE,
                                    message: {
                                        message: `Developer Message. Pricing Cached Date: ${formattedDate}`,
                                        type: "info",
                                    },
                                });
                            }

                            // If cache date is older than 2 weeks ? show admins only
                            if (ageInDays > 14 && app?.user?.Role >= 90) {
                                dispatch({
                                    type: SHOW_DESIGNER_MESSAGE,
                                    message: {
                                        message: `Pricing data is older than 2 weeks: ${formattedDate}`,
                                        type: "warning",
                                    },
                                });
                            }
                        }
                    }

                    dispatch(updateAPP("pricefile", RehydrateThumbnails(json))).then(() => {
                        dispatch({ type: UPDATE_DESIGNER, parameter: SHOW_LOADING_BACKDROP, value: false });
                    });
                    dispatch(updateAPP("pricefile_tier", RehydrateThumbnails(json))).then(() => {
                        dispatch({ type: UPDATE_DESIGNER, parameter: SHOW_LOADING_BACKDROP, value: false });
                    });
                })
                .catch(() => {
                    dispatch({ type: SHOW_DESIGNER_MESSAGE, message: "Error: Getting Pricing Error" });
                    dispatch({ type: UPDATE_DESIGNER, parameter: SHOW_LOADING_BACKDROP, value: false });
                });
        }

        // Price rise-specific logic
        if (isPriceRiseActive(app)) {
            if (
                job?.QuoteParameters?.BOM?.SystemList?.items?.length > 0 &&
                job?.QuoteParameters?.DeliveryDate &&
                isBeforePriceRiseStart(app) &&
                !isDeliveryDateInvalid(job)
            ) {
                const configUrl = `${API_BASE()}bom/pricing`;
                const payload = {
                    date: job?.QuoteParameters?.DeliveryDate,
                    items: job?.QuoteParameters?.BOM?.OrderList?.items ?? job?.QuoteParameters?.BOM?.SystemList?.items,
                    customercode: job?.Distributor?.LYVESAccount,
                };

                fetchTimeout(configUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                })
                    .then(async (response) => {
                        if (!response.ok) throw new Error();
                        const { totalPrice } = await response.json();
                        const newP = +totalPrice || 0;

                        const bom = job.QuoteParameters.BOM;
                        const list = bom.OrderList?.items ? bom.OrderList : bom.SystemList;
                        const oldP = +list.total_eprice || 0;

                        // absolute difference
                        const diff = +(newP - oldP).toFixed(2); // round to 2 decimal places

                        if (Math.abs(diff) >= 0.01) {
                            list.total_eprice = newP;

                            const pp = job.QuoteParameters.PRICING_PARAMETERS;
                            pp.MaterialFixedPrice = newP;
                            pp.OutputSummary.MaterialBase = pp.OutputSummary.MaterialSubtotal = newP;

                            dispatch(updateJobState("QuoteParameters.PRICING_PARAMETERS", "MaterialFixedPrice", newP));
                            dispatch(
                                updateJobState("QuoteParameters.PRICING_PARAMETERS", "OutputSummary", pp.OutputSummary)
                            );

                            if (app?.user?.Role === 99)
                                dispatch({
                                    type: SHOW_DESIGNER_MESSAGE,
                                    message: {
                                        type: "Success",
                                        message:
                                            diff > 0
                                                ? `Pricing has increased (+$${diff.toFixed(2)})`
                                                : `Pricing has decreased (-$${Math.abs(diff).toFixed(2)})`,
                                    },
                                });
                        }
                    })
                    .catch(() => {
                        dispatch({ type: SHOW_DESIGNER_MESSAGE, message: "Error: Getting Pricing Error" });
                        dispatch({ type: UPDATE_DESIGNER, parameter: SHOW_LOADING_BACKDROP, value: false });
                    });
            }
        }
    }, [
        job_loaded,
        job?.Customer?.DistLevel,
        job?.Distributor?.LYVESAccount,
        job?.QuoteParameters?.DeliveryDate,
        app?.pricefile?.LYVESAccount,
    ]);

    // Site param check
    useEffect(() => {
        const siteParams = job?.SiteParameters;
        if (job?.id && siteParams?.WindZoneAS1170 === null && !SHOW_CUSTOMER_JOB_PANEL) {
            dispatch(updateDesigner(SHOW_CUSTOMER_JOB_PANEL, true));
            openMessageBox({
                title: "Wind data not valid",
                message: "Wind Data is not valid. Please confirm before proceeding.",
            });
        }
    }, [job?.id, job?.SiteParameters]);

    // Window resize handling
    useEffect(() => {
        const handleResize = () => {
            const newWidthValue = window.innerWidth > 1400 ? 1300 : "100%";
            const newHeightValue = "98%";

            setNewWidth({ width: newWidthValue });
            setNewHeight({ height: newHeightValue });
        };
        
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        isResizingEWRef.current = isResizingEW;
        isResizingNSRef.current = isResizingNS;
        framePositionRef.current = framePosition;
    }, [isResizingEW, isResizingNS, framePosition]);

    useEffect(() => {
        let animationFrameId;

        const handleMouseMove = (e) => {
            if ((!isResizingEWRef.current && !isResizingNSRef.current) || !framePositionRef.current || animationFrameId)
                return;

            animationFrameId = requestAnimationFrame(() => {
                if (isResizingEWRef.current) {
                    setNewWidth({ width: e.clientX - framePositionRef.current.x + 2 });
                }

                if (isResizingNSRef.current) {
                    setNewHeight({ height: e.clientY - framePositionRef.current.y });
                }
                animationFrameId = null;
            });
        };

        const handleMouseUp = () => {
            setIsResizingEW(false);
            setIsResizingNS(false);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    useEffect(() => {
        const handleMouseUp = (e) => {
            if (window.isDesignerResizing) {
                let offsetLeft = e.clientX + 2;
                let offsetTop = e.clientY + 2;

                offsetLeft = Math.max(300, Math.min(offsetLeft, document.body.offsetWidth - 80));
                offsetTop = Math.max(200, Math.min(offsetTop, document.body.offsetHeight - 80));

                setNewWidth({ width: offsetLeft });
                setNewHeight({ height: offsetTop });
            }
            window.isDesignerResizing = false;
        };

        window.addEventListener("mouseup", handleMouseUp);
        return () => window.removeEventListener("mouseup", handleMouseUp);
    }, []);

    // Check address exists warning
    useEffect(() => {
        if (
            job &&
            job?.Customer?.Address?.FormattedAddress === "" &&
            job?.SiteParameters?.Address?.FormattedAddress === "" &&
            job?.SiteParameters?.WindData?.WindZoneAS1170?.Latlng_Short !== ""
        ) {
            dispatch({
                type: SHOW_DESIGNER_MESSAGE,
                message: {
                    type: "error",
                    timeout: 10000,
                    action: {
                        label: "Edit Details",
                        callback: () => dispatch(updateDesigner(SHOW_CUSTOMER_JOB_PANEL, true)),
                    },
                    message: "Address not set. Please set the address for this job.",
                },
            });
        }
    }, [job_loaded]);

    // Preprod warning
    useEffect(() => {
        if (job?.preprod && !job?.App?.Locked && !window.location.href.includes("preprod")) {
            openMessageBox({
                title: "JOB WAS CREATED IN PREPROD",
                message:
                    "Warning ?? this job was created in preprod. If testing please switch environments. Proceed with discretion.",
            });
        }
    }, [job_loaded]);

    // Dialog logic
    useEffect(() => {
        if (SHOW_DIALOG) {
            setDialogData(app?.designer?.DIALOG_DATA);
            setOpenDialog(SHOW_DIALOG);
        }
    }, [SHOW_DIALOG]);

    // Save job on param change
    useEffect(() => {
        setShouldSave(true);
    }, [colour_change, shape_update, job?.JobNotes]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            saveJobBasic(job, true, user_data);
            setShouldSave(false);
        }, 2000);
        return () => clearTimeout(timeout);
    }, [quoteParams]);

    // Customer form reset
    useEffect(() => {
        setTriggerCustoomerFormClose(false);
    }, [triggerCustoomerFormClose]);

    // Offering load
    useEffect(() => {
        if (job_distributor) {
            dispatch(loadOffering());
        }
    }, [job_distributor]);

    // Erp Number popup check
    useEffect(() => {
        if (
            job &&
            job?.ErpNumbers === undefined &&
            job.OrderStatus &&
            job?.OrderStatus === "ACCEPTED" &&
            app?.user?.Role >= 90
        ) {
            openMessageBox({
                title: "ERP Order Numbers",
                message: [
                    <div key="erp-editor">
                        <ErpEditorInternal
                            dispatch={dispatch}
                            prefix={job.Distributor.POA}
                            initialErpNumbers={job?.ErpNumbers}
                            onChange={(updated) => {
                                dispatch(
                                    updateJobParameter(null, {
                                        ErpNumbers: updated,
                                    })
                                );
                            }}
                        />
                    </div>,
                ],
                primaryActionText: "CLOSE",
            });
        }
    }, [job?.OrderStatus]);

    // Save interval
    useInterval(() => {
        if (shouldSave) {
            saveJobBasic(job, false, user_data);
            setShouldSave(false);
        }
    }, 10000);

    useEffect(() => {
        if (!bomOpen) {
            setBomIsPopOut(user_data?.preferences?.bomMode === "New Window");
        }
    }, [bomOpen, user_data?.preferences?.bomMode]);

    // Job check refresh interval
    useInterval(() => {
        if (!document.hidden && job?.App?.Locked) {
            dispatch(updateJobIfRequired());
        }
    }, 5000);
}

export default useDesignerHooks;
